import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useSignalRChat } from '../../Core/Hooks/useSignalRChat';
import { ChatRoom } from '../../Core/Types/admin/chat';
import { ChatSignalREventType } from '../../Core/Types/chat';
import './ChatWindow.scss';
import { differenceInCalendarDays } from 'date-fns';
import { getCultureCode } from '../../Core/Services/common';
import BizIconButton from '../../Core/Components/Mini/BizIconButton';
import { UserBaseLegacy } from '../../Core/Types/user';
import BizTextArea from '../../Core/Components/Mini/BizTextArea';
import { useTranslation } from 'react-i18next';
import { BizIcon } from '../../Core/Components/Mini/BizIcon';

type ChatWindowProps = {
	chatRoom: ChatRoom;
	currentUser: UserBaseLegacy;
	onClose?: () => void;
	chatFuncs: ReturnType<typeof useSignalRChat>;
	autoSetFocus?: boolean;
};

const ChatWindow = ({ chatRoom, currentUser, onClose, chatFuncs, autoSetFocus }: ChatWindowProps) => {
	const { t, i18n } = useTranslation();
	const culture = getCultureCode() || i18n.language;
	const [today, setToday] = useState(() => new Date());
	const [messageText, setMessageText] = useState('');
	const messagesListRef = useRef<HTMLDivElement>(null);
	const messageInputRef = useRef<HTMLTextAreaElement>(null);
	const pageScrollYBeforeFocusRef = useRef<number | null>(null);

	const { connectionState, isRoomReady, messages, sendChatMessage, joinRoom, leaveRoom } = chatFuncs;
	const roomMessages = messages.filter((message) => message.ChatRoomId === chatRoom.Id);
	useEffect(() => {
		const now = new Date();
		const nextDay = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
		const timeout = window.setTimeout(() => setToday(new Date()), nextDay.getTime() - now.getTime());
		return () => window.clearTimeout(timeout);
	}, [today]);

	const scrollMessagesToEnd = useCallback((behavior: ScrollBehavior = 'auto') => {
		const messagesList = messagesListRef.current;
		if (!messagesList) return;

		messagesList.scrollTo({
			top: messagesList.scrollHeight,
			behavior,
		});
	}, []);

	useEffect(() => {
		if (chatRoom.Id) joinRoom(chatRoom.Id);

		return () => {
			if (chatRoom.Id) leaveRoom(chatRoom.Id);
		};
	}, [chatRoom.Id, joinRoom, leaveRoom]);

	useEffect(() => {
		if (!autoSetFocus || !isRoomReady) return;

		const focusTimeout = window.setTimeout(() => {
			if (messageInputRef.current && !messageInputRef.current.disabled) {
				messageInputRef.current.focus({ preventScroll: true });
				const length = messageInputRef.current.value.length;
				messageInputRef.current.setSelectionRange(length, length);
				window.requestAnimationFrame(() => scrollMessagesToEnd());
			}
		}, 150);

		return () => window.clearTimeout(focusTimeout);
	}, [autoSetFocus, isRoomReady, chatRoom.Id, scrollMessagesToEnd]);

	useEffect(() => {
		if (messages.length > 0) {
			const animationFrameId = window.requestAnimationFrame(() => {
				scrollMessagesToEnd(autoSetFocus ? 'smooth' : 'auto');
			});

			return () => window.cancelAnimationFrame(animationFrameId);
		}
	}, [messages, autoSetFocus, scrollMessagesToEnd]);

	useEffect(() => {
		const messageInput = messageInputRef.current;
		if (!messageInput || typeof window === 'undefined') return;

		const rememberPageScrollPosition = () => {
			pageScrollYBeforeFocusRef.current = window.scrollY;
		};
		const handleFocus = () => {
			if (pageScrollYBeforeFocusRef.current === null) rememberPageScrollPosition();
			window.requestAnimationFrame(() => scrollMessagesToEnd());
		};
		const handleBlur = () => {
			pageScrollYBeforeFocusRef.current = null;
		};

		messageInput.addEventListener('pointerdown', rememberPageScrollPosition);
		messageInput.addEventListener('focus', handleFocus);
		messageInput.addEventListener('blur', handleBlur);

		return () => {
			messageInput.removeEventListener('pointerdown', rememberPageScrollPosition);
			messageInput.removeEventListener('focus', handleFocus);
			messageInput.removeEventListener('blur', handleBlur);
		};
	}, [scrollMessagesToEnd]);

	useEffect(() => {
		if (typeof window === 'undefined') return;

		const visualViewport = window.visualViewport;
		let firstAnimationFrameId: number | null = null;
		let secondAnimationFrameId: number | null = null;

		const handleViewportChange = () => {
			if (
				!window.matchMedia('(max-width: 960px)').matches ||
				document.activeElement !== messageInputRef.current ||
				pageScrollYBeforeFocusRef.current === null
			) {
				return;
			}

			if (firstAnimationFrameId !== null) window.cancelAnimationFrame(firstAnimationFrameId);
			if (secondAnimationFrameId !== null) window.cancelAnimationFrame(secondAnimationFrameId);
			firstAnimationFrameId = window.requestAnimationFrame(() => {
				secondAnimationFrameId = window.requestAnimationFrame(() => {
					window.scrollTo({
						top: pageScrollYBeforeFocusRef.current ?? window.scrollY,
						left: window.scrollX,
						behavior: 'auto',
					});
					scrollMessagesToEnd();
				});
			});
		};

		visualViewport?.addEventListener('resize', handleViewportChange);
		visualViewport?.addEventListener('scroll', handleViewportChange);
		window.addEventListener('resize', handleViewportChange);

		return () => {
			visualViewport?.removeEventListener('resize', handleViewportChange);
			visualViewport?.removeEventListener('scroll', handleViewportChange);
			window.removeEventListener('resize', handleViewportChange);
			if (firstAnimationFrameId !== null) window.cancelAnimationFrame(firstAnimationFrameId);
			if (secondAnimationFrameId !== null) window.cancelAnimationFrame(secondAnimationFrameId);
		};
	}, [scrollMessagesToEnd]);

	const handleSendMessage = () => {
		if (messageText.trim() && chatRoom.Id && sendChatMessage(chatRoom.Id, messageText.trim())) {
			setMessageText('');
		}
	};

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			handleSendMessage();
		}
	};

	const timeFormatter = new Intl.DateTimeFormat(culture, {
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
	});
	const weekdayFormatter = new Intl.DateTimeFormat(culture, { weekday: 'long' });
	const dateFormatter = new Intl.DateTimeFormat(culture, {
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
	});
	const formatMessageTime = (date: Date) => {
		const daysAgo = differenceInCalendarDays(today, date);
		const time = timeFormatter.format(date);
		if (daysAgo === 0) return time;
		if (daysAgo === 1) return `${t('CHAT_WINDOW.YESTERDAY', { defaultValue: 'Yesterday' })} ${time}`;
		if (daysAgo >= 2 && daysAgo <= 7) return `${weekdayFormatter.format(date)} ${time}`;
		return `${dateFormatter.format(date)} ${time}`;
	};

	const getConnectionStatus = () => {
		if (connectionState === 'DISCONNECTED') {
			return t('CHAT_WINDOW.CHAT_OFFLINE', {
				defaultValue: 'Chat is offline. Trying to reconnect…',
			});
		}
		if (connectionState === 'CONNECTING' || connectionState === 'RECONNECTING') {
			return t('CHAT_WINDOW.RECONNECTING_TO_CHAT', { defaultValue: 'Reconnecting to chat…' });
		}
		if (!isRoomReady) {
			return t('CHAT_WINDOW.SYNCHRONIZING_CHAT', { defaultValue: 'Synchronizing chat…' });
		}
		return null;
	};

	const connectionStatus = getConnectionStatus();

	return (
		<div className='biz-chat-window'>
			<div className='chat-header'>
				{onClose && (
					<span className='chat-back-action'>
						<BizIconButton
							icon='CHEVRON-LEFT'
							onClick={onClose}
							noBackground
							legacy={false}
							title={t('CHAT_WINDOW.BACK_TO_CHATS', { defaultValue: 'Back to chats' })}
						/>
					</span>
				)}
				<div className='chat-title'>
					<h4>{chatRoom.ExternalName}</h4>
				</div>
				{onClose && (
					<span className='chat-close-action'>
						<BizIconButton
							icon='REMOVE'
							onClick={onClose}
							noBackground
							legacy={false}
							title={t('CHAT_WINDOW.CLOSE_CHAT', { defaultValue: 'Close chat' })}
						/>
					</span>
				)}
			</div>

			{connectionStatus && (
				<div
					className={`chat-connection-status chat-connection-status--${connectionState.toLowerCase()}`}
					aria-live='polite'
				>
					{connectionStatus}
				</div>
			)}

			<div className='chat-body'>
				<div className='messages-container'>
					<div className='messages-list' data-messages-container ref={messagesListRef}>
						<div className='messages-content'>
							{roomMessages.length === 0 && !isRoomReady && (
								<div className='messages-loading'>
									<BizIcon icon='LOADING' />
									<span>{t('CHAT_WINDOW.LOADING_MESSAGES', { defaultValue: 'Loading messages…' })}</span>
								</div>
							)}
							{roomMessages.length === 0 && isRoomReady && (
								<div className='no-messages'>
									{t('CHAT_WINDOW.NO_MESSAGES_YET', {
										defaultValue: 'No messages yet. Start the conversation!',
									})}
								</div>
							)}
							{roomMessages
								.sort((a, b) => a.SentDateUTC.getTime() - b.SentDateUTC.getTime())
								.map((message, index, sortedMessages) => {
									const isSystemMessage =
										message.EventType && message.EventType >= ChatSignalREventType.UserJoined;
									const isOwnMessage = message.FromUserId === currentUser.Id;

									const showTodayDivider =
										differenceInCalendarDays(today, message.SentDateUTC) === 0 &&
										index > 0 &&
										differenceInCalendarDays(today, sortedMessages[index - 1].SentDateUTC) > 0;

									return (
										<Fragment key={`${message.Id}-${message.SentDateUTC.getTime()}`}>
											{showTodayDivider && (
												<div className='chat-day-divider'>
													<span>{t('CHAT_WINDOW.TODAY', { defaultValue: 'Today' })}</span>
												</div>
											)}
											<div
												className={`message ${
													isSystemMessage
														? 'system-message'
														: isOwnMessage
															? 'own-message'
															: 'other-message'
												}`}
											>
												<div className='message-header'>
													<span className='user-name'>{message.FromUserAlias}</span>
													<span className='message-time'>{formatMessageTime(message.SentDateUTC)}</span>
												</div>
												<div className='message-content'>{message.Message}</div>
											</div>
										</Fragment>
									);
								})}
						</div>
					</div>
				</div>
			</div>

			<div className='chat-input'>
				<div className='input-container'>
					<BizTextArea
						ref={messageInputRef}
						value={messageText}
						onChange={(e) => setMessageText(e.target.value)}
						onKeyUp={handleKeyPress}
						placeholder={t('CHAT_WINDOW.TYPE_MESSAGE_TO', {
							chatRoomName: chatRoom.ExternalName,
							defaultValue: 'Type a message to {{chatRoomName}}...',
						})}
						className='message-input'
						disabled={!isRoomReady}
					/>

					<BizIconButton
						icon='PAPER-PLANE'
						onClick={handleSendMessage}
						disabled={!messageText.trim() || !isRoomReady}
						title={t('CHAT_WINDOW.SEND_MESSAGE', { defaultValue: 'Send message' })}
						isLead={true}
						legacy={false}
						noBackground={true}
					/>
				</div>
			</div>
		</div>
	);
};

export default ChatWindow;
